import { FirebaseError } from "firebase/app";

import i18n from "@/i18n";

/**
 * Domain error returned by every `lib/api` call.
 *
 * Carries the raw Firebase callable code (e.g. `functions/already-exists`)
 * plus a human-readable `message` already tailored for display. Forms use
 * `isSlugError` to decide whether to map the failure onto the slug input
 * or just toast it.
 */
export class AppError extends Error {
  readonly code: string;
  readonly isSlugError: boolean;

  constructor(code: string, message: string, isSlugError: boolean) {
    super(message);
    this.name = "AppError";
    this.code = code;
    this.isSlugError = isSlugError;
  }

  /** True when the backend rejected the call because the user is not on the admin list. */
  get isPermissionDenied(): boolean {
    return this.code === "functions/permission-denied" || this.code === "permission-denied";
  }
}

/** Map a Firebase callable error onto an `AppError` with display-ready copy. */
export function toAppError(err: unknown): AppError {
  if (err instanceof AppError) return err;

  const { code, message } = readError(err);

  const isSlugError = code === "functions/already-exists";

  let displayMessage = message;

  if (code === "functions/already-exists") {
    displayMessage = i18n.t("errors.slugTaken");
  } else if (code === "functions/permission-denied" || code === "permission-denied") {
    displayMessage = i18n.t("errors.notAdmin");
  } else if (code === "functions/unauthenticated" || code === "unauthenticated") {
    displayMessage = i18n.t("errors.signIn");
  } else if (code === "functions/deadline-exceeded" || code === "deadline-exceeded") {
    displayMessage = i18n.t("errors.deadlineExceeded");
  } else if (code === "functions/resource-exhausted" || code === "resource-exhausted") {
    displayMessage = i18n.t("errors.aiQuota");
  } else if (!displayMessage) {
    displayMessage = i18n.t("errors.generic");
  }

  return new AppError(code, displayMessage, isSlugError);
}

/**
 * Map a Firebase Auth error (`auth/*` code) onto display-ready copy for the
 * login form. Falls back to a generic message for unknown codes instead of
 * leaking the raw "Firebase: Error (auth/...)." string.
 */
export function authErrorMessage(err: unknown): string {
  const { code } = readError(err);

  switch (code) {
    case "auth/invalid-credential":
    case "auth/invalid-login-credentials":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return i18n.t("errors.auth.invalidCredentials");
    case "auth/invalid-email":
      return i18n.t("errors.auth.invalidEmail");
    case "auth/email-already-in-use":
      return i18n.t("errors.auth.emailInUse");
    case "auth/weak-password":
      return i18n.t("errors.auth.weakPassword");
    case "auth/missing-password":
      return i18n.t("errors.auth.missingPassword");
    case "auth/user-disabled":
      return i18n.t("errors.auth.userDisabled");
    case "auth/expired-action-code":
      return i18n.t("errors.auth.expiredActionCode");
    case "auth/invalid-action-code":
      return i18n.t("errors.auth.invalidActionCode");
    case "auth/too-many-requests":
      return i18n.t("errors.auth.tooManyRequests");
    case "auth/network-request-failed":
      return i18n.t("errors.auth.networkError");
    default:
      return i18n.t("errors.auth.generic");
  }
}

function readError(err: unknown): { code: string; message: string } {
  if (err instanceof FirebaseError) {
    return { code: err.code, message: err.message ?? "" };
  }
  if (err instanceof Error) {
    const maybeCoded = err as Error & { code?: unknown };
    const code = typeof maybeCoded.code === "string" ? maybeCoded.code : "unknown";
    return { code, message: err.message };
  }
  return { code: "unknown", message: "" };
}
